/**
 * Hands "light this row" from a surface that has just saved to the list that shows the row.
 *
 * The saver and the list are usually not on screen together: an editor's close unmounts it and
 * mounts its landing fresh, so the request is left for a list that is about to mount. Sometimes the
 * list is already there (a dialog over it), so it listens and takes the request at once. Either way
 * the list passes what it resolves to `revealRowKey`.
 *
 * A request left for a list is only good for a moment — the close that wrote it is about to show
 * the list. One that finds no list within `handoverMs` (the save closed onto some other screen) is
 * dropped, so a later visit to the list does not light an old row.
 *
 * One handover per list surface, made once at module level: it is a single slot, so two lists
 * sharing one would take each other's requests.
 */
export interface IRevealHandover<TRequest> {
  /** The saver has succeeded and is about to close. */
  request: (request: TRequest) => void;
  /** The list is mounting. Returns the request left for it, once, if it is still fresh. */
  take: () => TRequest | null;
  /** The list is on screen: requests reach it directly. Returns the unsubscribe for unmount. */
  subscribe: (onReveal: (request: TRequest) => void) => () => void;
}

interface IPendingReveal<TRequest> {
  request: TRequest;
  requestedAt: number;
}

/** How long a left request waits for its list to mount before it is dropped. */
export const REVEAL_HANDOVER_MS: number = 5000;

export const createRevealHandover = <TRequest>(handoverMs: number = REVEAL_HANDOVER_MS): IRevealHandover<TRequest> => {
  let pending: IPendingReveal<TRequest> | null = null;
  let listener: ((request: TRequest) => void) | null = null;

  return {
    request: (request: TRequest): void => {
      if (listener !== null) {
        pending = null;
        listener(request);
        return;
      }
      pending = { request, requestedAt: Date.now() };
    },
    take: (): TRequest | null => {
      const left: IPendingReveal<TRequest> | null = pending;
      pending = null;
      if (left === null) return null;
      return Date.now() - left.requestedAt <= handoverMs ? left.request : null;
    },
    subscribe: (onReveal: (request: TRequest) => void): (() => void) => {
      listener = onReveal;
      return (): void => {
        if (listener === onReveal) {
          listener = null;
        }
      };
    }
  };
};
